import { parse, SyntaxError } from 'liqe'
import {
  E_LUCENE_SYNTAX_EXCEPTION,
  E_LUCENE_UNEXPECTED_EXCEPTION,
  E_LUCENE_INVALID_TYPE,
  E_LUCENE_REGEX_NOT_SUPPORTED,
} from '@nhtio/lucid-resourceful/errors'
import type { QueryClientContract } from '@adonisjs/lucid/types/database'
import type { LucidModel, ModelKeysContract } from '@adonisjs/lucid/types/model'
import type { DatabaseQueryBuilderContract } from '@adonisjs/lucid/types/querybuilder'
import type {
  LiqeQuery,
  ComparisonOperatorToken,
  EmptyExpression,
  FieldToken,
  ImplicitFieldToken,
  LiteralExpressionToken,
  LogicalExpressionToken,
  RangeExpressionToken,
  RegexExpressionToken,
  TagToken,
  UnaryOperatorToken,
  ParenthesizedExpressionToken,
  ExpressionToken,
  ParserAst,
} from 'liqe'

/**
 * Database query builder contract extended for Lucene filtering capabilities.
 *
 * @template Result - The result type for the query builder
 */
export interface LuceneFilteredDatabaseQueryBuilderContract extends DatabaseQueryBuilderContract {}

export class LuceneLucidQueryBuilder {
  readonly #attributesToColumns: ModelKeysContract
  readonly #allowedColumns?: string[] | undefined
  readonly #connection: QueryClientContract
  readonly #dialect: string
  // @ts-ignore - not used yet
  readonly #primaryKey: string
  // @ts-ignore - not used yet
  readonly #tableName: string
  // @ts-ignore - not used yet
  readonly #model?: LucidModel | undefined

  constructor(
    connection: QueryClientContract,
    query: LuceneFilteredDatabaseQueryBuilderContract,
    attributesToColumns: ModelKeysContract,
    primaryKey: string,
    tableName: string,
    allowedColumns?: string[] | undefined,
    model?: LucidModel | undefined
  ) {
    this.#attributesToColumns = attributesToColumns
    this.#primaryKey = primaryKey
    this.#tableName = tableName
    this.#allowedColumns = allowedColumns
    this.#model = model
    this.#connection = connection
    this.#dialect = query.client.dialect.name
  }

  applyLuceneAst(
    ast: ParserAst,
    query: LuceneFilteredDatabaseQueryBuilderContract,
    throwOnInvalidType: boolean = false
  ) {
    switch (ast.type) {
      case 'EmptyExpression':
        this.applyLuceneEmptyExpressionAst(ast, query)
        break

      case 'LogicalExpression':
        this.applyLuceneLogicalExpressionAst(ast, query)
        break

      case 'ParenthesizedExpression':
        this.applyLuceneParenthesizedExpressionAst(ast, query)
        break

      case 'UnaryOperator':
        this.applyLuceneUnaryOperatorAst(ast, query)
        break

      case 'Tag':
        this.applyLuceneTagAst(ast, query)
        break

      default:
        if (throwOnInvalidType) {
          throw new E_LUCENE_INVALID_TYPE((ast as any).type)
        }
        break
    }
  }

  applyLuceneEmptyExpressionAst(
    _ast: EmptyExpression,
    _query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    // No operation for empty expressions
  }

  applyLuceneLogicalExpressionAst(
    ast: LogicalExpressionToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    const operatorType = ast.operator.operator
    query.where((subQuery) => {
      if (operatorType === 'AND') {
        this.applyLuceneAst(ast.left, subQuery)
        this.applyLuceneAst(ast.right, subQuery)
      } else {
        subQuery.orWhere((orSubQuery) => {
          this.applyLuceneAst(ast.left, orSubQuery)
        })
        subQuery.orWhere((orSubQuery) => {
          this.applyLuceneAst(ast.right, orSubQuery)
        })
      }
    })
  }

  applyLuceneParenthesizedExpressionAst(
    ast: ParenthesizedExpressionToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    query.where((subQuery) => {
      this.applyLuceneAst(ast.expression, subQuery)
    })
  }

  applyLuceneUnaryOperatorAst(
    ast: UnaryOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    const operator = ast.operator
    if (operator === 'NOT' || operator === '-') {
      query.whereNot((subQuery) => {
        this.applyLuceneAst(ast.operand, subQuery)
      })
    } else {
      query.where((subQuery) => {
        this.applyLuceneAst(ast.operand, subQuery)
      })
    }
  }

  applyLuceneTagAst(ast: TagToken, query: LuceneFilteredDatabaseQueryBuilderContract) {
    const { expression, field, operator } = ast
    switch (field.type) {
      case 'ImplicitField':
        this.applyLuceneImplicitFieldAst(field, expression, operator, query)
        break

      case 'Field':
        this.applyLuceneFieldAst(field, expression, operator, query)
        break
    }
  }

  #getSearchableColumns() {
    if (
      this.#model &&
      '$resourcefulColumns' in this.#model &&
      this.#model.$resourcefulColumns instanceof Map
    ) {
      const modelFields: string[] = []
      this.#model.$resourcefulColumns.forEach((_c, k) => {
        modelFields.push(k as string)
      })
      if (
        '$resourcefulRelationships' in this.#model &&
        this.#model.$resourcefulRelationships instanceof Map
      ) {
        this.#model.$resourcefulRelationships.forEach((_r, k) => {
          modelFields.push(k)
        })
      }
      return Array.from(new Set(modelFields)).filter(
        (f) => !this.#allowedColumns || this.#allowedColumns.includes(f)
      )
    }
    return Object.entries(this.#attributesToColumns.all())
      .filter(([attr]) => !this.#allowedColumns || this.#allowedColumns.includes(attr))
      .map(([, column]) => column)
  }

  applyLuceneImplicitFieldAst(
    _field: ImplicitFieldToken,
    expression: ExpressionToken,
    operator: ComparisonOperatorToken | undefined,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    const searchableColumns = this.#getSearchableColumns()
    const defaultOperator: ComparisonOperatorToken = operator || {
      operator: ':',
      type: 'ComparisonOperator',
      location: { start: 0, end: 0 },
    }
    searchableColumns.forEach((columnName) => {
      const fakeField: FieldToken = {
        name: columnName,
        type: 'Field',
        location: { start: 0, end: 0 },
        quoted: false,
      }
      query.orWhere((s) => {
        this.applyLuceneFieldAst(fakeField, expression, defaultOperator, s, undefined, true)
      })
    })
  }

  applyLuceneFieldAst(
    field: FieldToken,
    expression: ExpressionToken,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract,
    forModel?: LucidModel | undefined,
    isImplicit: boolean = false
  ) {
    const columnName = !forModel
      ? this.#attributesToColumns.get(field.name)
      : forModel.$keys.attributesToColumns.get(field.name)
    if (
      !columnName ||
      (!forModel && this.#allowedColumns && !this.#allowedColumns.includes(field.name))
    ) {
      if (field.name.includes('.')) {
        const parts = field.name
          .split('.')
          .filter((p) => 'string' === typeof p && p.trim().length > 0)
          .map((p) => p.trim())
        const relationship = parts.shift()
        if (!relationship || parts.length === 0) return
        this.applyLuceneRelatedFieldAst(
          relationship,
          parts.join('.'),
          expression,
          operator,
          query,
          forModel
        )
      }
      return
    }
    switch (expression.type) {
      case 'EmptyExpression':
        this.applyLuceneEmptyExpression(columnName, expression, operator, query)
        break
      case 'LiteralExpression':
        this.applyLuceneLiteralExpression(columnName, expression, operator, query, isImplicit)
        break
      case 'RangeExpression':
        this.applyLuceneRangeExpression(columnName, expression, operator, query)
        break
      case 'RegexExpression':
        this.applyLuceneRegexExpression(columnName, expression, operator, query)
        break
    }
  }

  applyLuceneRelatedFieldAst(
    relationship: string,
    relatedProperty: string,
    expression: ExpressionToken,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract,
    forModel?: LucidModel | undefined
  ) {
    if (!forModel && !this.#model) return
    const targetModel = forModel! || this.#model!
    const lucidRelationshipDefinition = targetModel.$relationsDefinitions.get(relationship)
    if (!lucidRelationshipDefinition) return
    lucidRelationshipDefinition.boot()
    const sub = lucidRelationshipDefinition.subQuery(this.#connection)
    const relatedField: FieldToken = {
      name: relatedProperty,
      type: 'Field',
      location: { start: 0, end: 0 },
      quoted: false,
    }
    const relatedModel = lucidRelationshipDefinition.relatedModel()
    this.applyLuceneFieldAst(relatedField, expression, operator, sub as any, relatedModel)
    query.whereExists(sub.prepare())
  }

  applyLuceneEmptyExpression(
    column: string,
    _expression: EmptyExpression,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    switch (operator.operator) {
      case ':':
      case ':=':
      case ':<=':
      case ':>=':
        query.where((sub) => {
          sub.whereNull(column)
          sub.orWhere(column, '=', '')
        })
        break
    }
  }

  applyLuceneLiteralExpression(
    column: string,
    expression: LiteralExpressionToken,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract,
    isImplicit = false
  ) {
    let value = expression.value
    let isCaseSensitive = false
    if (value === null) {
      /*
    |--------------------------------------------------------------------------
    | Treat null values as empty strings or zero depending on the operator
    |--------------------------------------------------------------------------
    */
      switch (operator.operator) {
        case ':':
        case ':=':
          query.if(
            ['postgres', 'pg'].includes(this.#dialect),
            (q) => {
              q.where((sub) => {
                sub.whereNull(column)

                // Numeric branch: compare text rep to '0'
                sub.orWhereRaw(
                  'pg_typeof(??) IN (' +
                    "'smallint'::regtype,'integer'::regtype,'bigint'::regtype," +
                    "'numeric'::regtype,'real'::regtype,'double precision'::regtype" +
                    ") AND COALESCE(??::text,'') = '0'",
                  [column, column]
                )

                // Non-numeric branch: empty string after cast
                sub.orWhereRaw(
                  'pg_typeof(??) NOT IN (' +
                    "'smallint'::regtype,'integer'::regtype,'bigint'::regtype," +
                    "'numeric'::regtype,'real'::regtype,'double precision'::regtype" +
                    ") AND COALESCE(??::text,'') = ''",
                  [column, column]
                )
              })
            },
            () => {
              query.if(
                ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                (q2) => {
                  q2.where((sub) => {
                    sub.whereNull(column)
                    sub.orWhere(column, '=', '')
                    /*
                     |--------------------------------------------------------------------------
                     | MySQL: Only match 0 if the column value is actually numeric
                     | Check that the value equals 0 AND its string representation equals '0'
                     | This prevents non-numeric strings from matching via type coercion
                     |--------------------------------------------------------------------------
                     */
                    sub.orWhereRaw('(?? = 0 AND CAST(?? AS CHAR) = ?)', [column, column, '0'])
                  })
                },
                (q2) => {
                  q2.where((sub) => {
                    sub.whereNull(column)
                    sub.orWhere(column, '=', '')
                    sub.orWhere(column, '=', 0)
                  })
                }
              )
            }
          )
          break
        case ':<':
          query.where(column, '<', 0)
          break
        case ':<=':
          query.where(column, '<=', 0)
          break
        case ':>':
          query.where(column, '>', 0)
          break
        case ':>=':
          query.where(column, '>=', 0)
          break
      }
      return
    }
    if ('string' === typeof value && expression.quoted) {
      isCaseSensitive = true
      /*
      |--------------------------------------------------------------------------
      | Strip quotes from the value because they are not needed
      |--------------------------------------------------------------------------
      */
      if (expression.quotes === 'double') {
        value = value.replace(/^"/g, '').replace(/"$/g, '')
      } else if (expression.quotes === 'single') {
        value = value.replace(/^'/g, '').replace(/'$/g, '')
      }
    } else if (
      /*
      |--------------------------------------------------------------------------
      | Special case for handling unquoted string literals with : or := operators
      | to perform a contains search by wrapping the value with wildcards (*)
      |--------------------------------------------------------------------------
      */
      isImplicit &&
      'string' === typeof value &&
      !expression.quoted &&
      [':', ':='].includes(operator.operator)
    ) {
      value = `*${value}*`
    }
    switch (typeof value) {
      case 'string':
        switch (operator.operator) {
          case ':':
          case ':=':
            // Check if the value contains wildcards
            if (value.includes('*') || value.includes('?')) {
              // Escape any instances of `%` and `_` in the literal value
              let likeValue = value.replace(/%/g, '\\%').replace(/_/g, '\\_')
              // Convert Lucene wildcards (*) to SQL wildcards (%) and (?) to (_)
              likeValue = likeValue.replace(/\*/g, '%').replace(/\?/g, '_')
              if (isCaseSensitive) {
                query.if(
                  ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                  (q) => {
                    /*
                     |--------------------------------------------------------------------------
                     | MySQL: Use whereRaw to bypass Knex's broken utf8_bin collation
                     | LIKE is already case-sensitive in MySQL by default
                     |--------------------------------------------------------------------------
                     */
                    q.whereRaw('?? LIKE ?', [column, likeValue])
                  },
                  (q) => {
                    q.whereLike(column, likeValue)
                  }
                )
              } else {
                query.if(
                  ['postgres', 'pg'].includes(this.#dialect),
                  (q) => {
                    // q.whereILike(column, likeValue)
                    /*
                     |--------------------------------------------------------------------------
                     | Fix to cast column to text to avoid issues with non-text columns
                     | (e.g., integer, boolean) and ensure that NULL values are handled correctly.
                     |--------------------------------------------------------------------------
                     */
                    q.whereRaw("COALESCE(??::text, '') ILIKE ?", [column, likeValue])
                  },
                  () => {
                    query.if(
                      ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                      (q2) => {
                        /*
                         |--------------------------------------------------------------------------
                         | MySQL: Use LOWER() for case-insensitive comparison
                         |--------------------------------------------------------------------------
                         */
                        q2.whereRaw('LOWER(??) LIKE LOWER(?)', [column, likeValue])
                      },
                      (q2) => {
                        q2.whereLike(column, likeValue)
                      }
                    )
                  }
                )
                query
              }
            } else {
              query.where((sub) => {
                if (isCaseSensitive) {
                  query.if(
                    ['postgres', 'pg'].includes(this.#dialect),
                    () => {
                      // sub.orWhereILike(column, value)
                      /*
                       |--------------------------------------------------------------------------
                       | Fix to cast column to text to avoid issues with non-text columns
                       | (e.g., integer, boolean) and ensure that NULL values are handled correctly.
                       |--------------------------------------------------------------------------
                       */
                      sub.whereRaw("COALESCE(??::text, '') = ?", [column, value])
                    },
                    () => {
                      query.if(
                        ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                        () => {
                          /*
                          |--------------------------------------------------------------------------
                          | Cast all columns to text for comparison
                          |--------------------------------------------------------------------------
                          */
                          sub.whereRaw('CAST(?? AS CHAR) = ?', [column, value])
                        },
                        () => {
                          /*
                          |--------------------------------------------------------------------------
                          | For exact matches (no wildcards), use = operator
                          |--------------------------------------------------------------------------
                          */
                          sub.where(column, '=', value)
                        }
                      )
                    }
                  )
                } else {
                  query.if(
                    ['postgres', 'pg'].includes(this.#dialect),
                    () => {
                      // sub.orWhereILike(column, value)
                      /*
                       |--------------------------------------------------------------------------
                       | Fix to cast column to text to avoid issues with non-text columns
                       | (e.g., integer, boolean) and ensure that NULL values are handled correctly.
                       |--------------------------------------------------------------------------
                       */
                      sub.whereRaw("COALESCE(??::text, '') ILIKE ?", [column, value])
                    },
                    () => {
                      query.if(
                        ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                        () => {
                          /*
                           |--------------------------------------------------------------------------
                           | MySQL: Use LOWER() for case-insensitive comparison
                           |--------------------------------------------------------------------------
                           */
                          sub.whereRaw('LOWER(??) LIKE LOWER(?)', [column, value])
                        },
                        () => {
                          sub.whereLike(column, value)
                        }
                      )
                    }
                  )
                }
              })
            }
            break
          case ':<':
            query.where(column, '<', value)
            break
          case ':<=':
            query.where(column, '<=', value)
            break
          case ':>':
            query.where(column, '>', value)
            break
          case ':>=':
            query.where(column, '>=', value)
            break
        }
        break
      case 'number':
        switch (operator.operator) {
          case ':':
          case ':=':
            if (isImplicit) {
              query.where((sub) => {
                if (isCaseSensitive) {
                  query.if(
                    ['postgres', 'pg'].includes(this.#dialect),
                    () => {
                      // sub.orWhereILike(column, value)
                      /*
                       |--------------------------------------------------------------------------
                       | Fix to cast column to text to avoid issues with non-text columns
                       | (e.g., integer, boolean) and ensure that NULL values are handled correctly.
                       |--------------------------------------------------------------------------
                       */
                      sub.whereRaw("COALESCE(??::text, '') = ?", [column, String(value)])
                    },
                    () => {
                      query.if(
                        ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                        () => {
                          /*
                          |--------------------------------------------------------------------------
                          | Cast all columns to text for comparison
                          |--------------------------------------------------------------------------
                          */
                          sub.whereRaw('CAST(?? AS CHAR) = ?', [column, String(value)])
                        },
                        () => {
                          /*
                          |--------------------------------------------------------------------------
                          | For exact matches (no wildcards), use = operator
                          |--------------------------------------------------------------------------
                          */
                          sub.where(column, '=', String(value))
                        }
                      )
                    }
                  )
                } else {
                  query.if(
                    ['postgres', 'pg'].includes(this.#dialect),
                    () => {
                      // sub.orWhereILike(column, value)
                      /*
                       |--------------------------------------------------------------------------
                       | Fix to cast column to text to avoid issues with non-text columns
                       | (e.g., integer, boolean) and ensure that NULL values are handled correctly.
                       |--------------------------------------------------------------------------
                       */
                      sub.whereRaw("COALESCE(??::text, '') ILIKE ?", [column, String(value)])
                    },
                    () => {
                      query.if(
                        ['mysql', 'mysql2', 'mariadb'].includes(this.#dialect),
                        () => {
                          /*
                           |--------------------------------------------------------------------------
                           | MySQL: Use LOWER() for case-insensitive comparison
                           |--------------------------------------------------------------------------
                           */
                          sub.whereRaw('LOWER(??) LIKE LOWER(?)', [column, String(value)])
                        },
                        () => {
                          sub.whereLike(column, value)
                        }
                      )
                    }
                  )
                }
              })
            } else {
              query.where(column, '=', value)
            }
            break
          case ':<':
            query.where(column, '<', value)
            break
          case ':<=':
            query.where(column, '<=', value)
            break
          case ':>':
            query.where(column, '>', value)
            break
          case ':>=':
            query.where(column, '>=', value)
            break
        }
        break
      case 'boolean':
        switch (operator.operator) {
          case ':':
          case ':=':
            query.where(column, '=', value)
            break
          case ':<':
            query.where(column, '<', value)
            break
          case ':<=':
            query.where(column, '<=', value)
            break
          case ':>':
            query.where(column, '>', value)
            break
          case ':>=':
            query.where(column, '>=', value)
            break
        }
        break
    }
  }

  applyLuceneRangeExpression(
    column: string,
    expression: RangeExpressionToken,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    const comparableMin = expression.range.minInclusive
      ? expression.range.min
      : expression.range.min + 1
    const comparableMax = expression.range.maxInclusive
      ? expression.range.max
      : expression.range.max - 1
    switch (operator.operator) {
      case ':':
      case ':=':
        query.whereBetween(column, [comparableMin, comparableMax])
        break
    }
  }

  applyLuceneRegexExpression(
    column: string,
    expression: RegexExpressionToken,
    operator: ComparisonOperatorToken,
    query: LuceneFilteredDatabaseQueryBuilderContract
  ) {
    let value = expression.value
    switch (this.#dialect) {
      case 'mysql':
      case 'mariadb':
        // MySQL uses REGEXP for regex matching
        switch (operator.operator) {
          case ':':
          case ':=':
            query.whereRaw(`?? REGEXP ?`, [column, value.replace(/\/(.*)\/.*/, '$1')])
            break
        }
        break

      case 'pg':
      case 'postgres':
        // PostgreSQL uses ~ for case-sensitive regex matching and ~* for case-insensitive regex matching
        switch (operator.operator) {
          case ':':
          case ':=':
            {
              const op = value.endsWith('i') ? '~*' : '~'
              const cleanedValue = value.replace(/\/(.*)\/.*/, '$1')
              query.whereRaw(`?? ${op} ?`, [column, cleanedValue])
            }
            break
        }
        break

      case 'oracle':
      case 'oracledb':
      case 'tedious':
      case 'mssql':
        // Oracle uses REGEXP_LIKE for regex matching
        switch (operator.operator) {
          case ':':
          case ':=':
            query.whereRaw(`REGEXP_LIKE(??, ?)`, [column, value])
            break
        }
        break

      case 'sqlite3':
      case 'better-sqlite3':
        throw new E_LUCENE_REGEX_NOT_SUPPORTED()

      default:
        // Default case for other dialects, using LIKE as a fallback
        // Note: This is not a true regex match, but a LIKE pattern match
        // Escape any instances of `%` and `_` in the regex value
        value = value.replace(/%/g, '\\%').replace(/_/g, '\\_')
        // Replace "*" with "%" and "?" with "_"
        value = value.replace(/\*/g, '%').replace(/\?/g, '_')
        switch (operator.operator) {
          case ':':
          case ':=':
            query.whereLike(column, value)
            break
        }
        break
    }
  }

  static get(
    luceneQuery: string,
    connection: QueryClientContract,
    attributesToColumns: ModelKeysContract,
    primaryKey: string,
    tableName: string,
    allowedColumns?: string[] | undefined,
    model?: LucidModel | undefined
  ): LuceneFilteredDatabaseQueryBuilderContract {
    let parsed: LiqeQuery
    try {
      parsed = parse(luceneQuery)
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new E_LUCENE_SYNTAX_EXCEPTION(luceneQuery, error)
      }
      throw new E_LUCENE_UNEXPECTED_EXCEPTION(error)
    }
    const query: LuceneFilteredDatabaseQueryBuilderContract = connection.query().from(tableName)
    const builder = new LuceneLucidQueryBuilder(
      connection,
      query,
      attributesToColumns,
      primaryKey,
      tableName,
      allowedColumns,
      model
    )
    builder.applyLuceneAst(parsed, query, true)
    return query
  }
}

/**
 * Converts a Lucene query string to a Lucid database query builder.
 * Parses the Lucene syntax and applies the resulting AST to generate SQL conditions.
 *
 * @template T - The result type for the query builder
 * @param luceneQuery - The Lucene query string to parse and convert
 * @param connection - The database connection client for creating queries
 * @param attributesToColumns - Mapping of model attributes to database columns
 * @param primaryKey - The primary key column name for implicit field queries
 * @param tableName - The database table name to query
 * @param allowedColumns - Optional array of allowed column names for security filtering
 * @param model - Optional The lucid model which the query is being built for. When passed, allows relationship-based filtration.
 * @returns A database query builder with the Lucene conditions applied
 * @throws {E_LUCENE_SYNTAX_EXCEPTION} When the Lucene query has syntax errors
 * @throws {E_LUCENE_UNEXPECTED_EXCEPTION} When an unexpected error occurs during parsing
 * @throws {E_LUCENE_INVALID_TYPE} When an unrecognized AST node type is encountered
 */
export const luceneToLucid = (
  luceneQuery: string,
  connection: QueryClientContract,
  attributesToColumns: ModelKeysContract,
  primaryKey: string,
  tableName: string,
  allowedColumns?: string[] | undefined,
  model?: LucidModel | undefined
): LuceneFilteredDatabaseQueryBuilderContract => {
  const query = LuceneLucidQueryBuilder.get(
    luceneQuery,
    connection,
    attributesToColumns,
    primaryKey,
    tableName,
    allowedColumns,
    model
  )
  query.whereNotNull(primaryKey)
  return query
}
